import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useDeviceConfigurationCache } from '../configuration/useDeviceConfigurationCache';
import { createMissingInitRhinoError } from '../errors';

import { createMissingPersonalListTypeError } from './errors';
import { setPersonalLineupQueryData } from './mutations';
import {
  CreatePersonalLineupArgs,
  createPersonalLineup as requestCreatePersonalLineup,
  deletePersonalLineup as requestDeletePersonalLineup,
} from './queryFunctions';
import { usePersonalLineup } from './usePersonalLineup';

type PersonalLineup = CreatePersonalLineupArgs['personalLineup'];

export function useCreatePersonalLineup() {
  const { configuration } = useDeviceConfigurationCache();
  // no need to trigger fetch
  const { personalLineup } = usePersonalLineup({ staleTime: Infinity });

  const handleSuccess = useCallback((id: number, personalLineup: PersonalLineup) => {
    const dictionary = new Map<number, number>();
    let highestChannelNumber = 0;

    for (let i = 0; i < personalLineup.length; i++) {
      const entry = personalLineup[i];

      if (entry.channelNumber > highestChannelNumber) {
        highestChannelNumber = entry.channelNumber;
      }

      dictionary.set(entry.channelId, entry.channelNumber);
    }

    setPersonalLineupQueryData({
      allLineups: [id],
      entries: personalLineup,
      dictionary,
      highestChannelNumber,
    });
  }, []);

  const mutationFn = useCallback(
    async (newLineup: PersonalLineup): Promise<number> => {
      if (!configuration) {
        throw createMissingInitRhinoError();
      }

      const listType = configuration.personalLists?.userPersonalLineup;

      if (!listType) {
        throw createMissingPersonalListTypeError();
      }

      const result = await requestCreatePersonalLineup({
        listType,
        personalLineup: newLineup,
      });

      if (!personalLineup) {
        return result;
      }

      // once lineup is submitted we need to remove the old ones
      Promise.allSettled(personalLineup.allLineups.map(requestDeletePersonalLineup));

      return result;
    },
    [personalLineup, configuration],
  );

  const { mutateAsync, isPending } = useMutation({
    mutationFn: mutationFn,
    onSuccess: handleSuccess,
  });

  const createPersonalLineup = useCallback(
    async (personalLineup: PersonalLineup) => {
      await mutateAsync(personalLineup);
    },
    [mutateAsync],
  );

  return {
    createPersonalLineup,
    isCreating: isPending,
  };
}
